import { request } from '@/utils/request'

// 定义流式聊天块类型
export interface EnglishLearningStreamChunk {
  text: string
  isComplete: boolean
}

/**
 * 英语学习集成流式聊天和语音合成
 * @param sessionId 会话ID
 * @param message 用户消息
 * @param childAge 儿童年龄
 * @param englishLevel 英语水平
 * @param learningMode 学习模式
 * @param conversationContext 对话上下文
 * @param textOnlyMode 是否为纯文字模式（不生成音频）
 * @param onTextChunk 文本块回调
 * @param onAudioChunk 音频块回调
 * @param onComplete 完成回调
 * @param onError 错误回调
 */
export const englishLearningStreamChatWithVoice = async (
  sessionId: number,
  message: string,
  childAge: number,
  englishLevel: string,
  learningMode: string,
  conversationContext: Array<{role: string, content: string}>,
  textOnlyMode: boolean,
  onTextChunk: (chunk: EnglishLearningStreamChunk) => void,
  onAudioChunk: (audioData: string) => void,
  onComplete: () => void,
  onError: (error: string) => void
) => {
  // 获取当前页面协议
  const currentProtocol = window.location.protocol;
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // 构建WebSocket URL
  let wsProtocol = 'ws://';
  if (currentProtocol === 'https:' || isLocalhost) {
    wsProtocol = 'wss://';
  }
  
  // 构建完整的WebSocket URL
  const baseUrl = `${wsProtocol}${window.location.host}`;
  let fullWsUrl = `${baseUrl}/api/english-learning/stream`;
  
  // 添加token参数
  const token = localStorage.getItem('token')
  if (!token) {
    onError('未找到认证令牌，请重新登录')
    return null
  }
  
  fullWsUrl += `?token=${token}`;
  
  console.log('【英语学习】尝试连接WebSocket:', fullWsUrl)
  
  const ws = new WebSocket(fullWsUrl)
  
  ws.onopen = () => {
    console.log('【英语学习】WebSocket连接建立成功')
    
    // 发送用户消息，包含学习模式、对话上下文和文字模式标志
    const messageData = {
      sessionId,
      message,
      childAge,
      englishLevel,
      learningMode,  // 添加学习模式
      conversationContext: conversationContext || [],  // 添加对话上下文
      textOnlyMode   // 添加文字模式标志
    }
    
    ws.send(JSON.stringify(messageData))
    console.log('【英语学习】已发送消息:', messageData)
    console.log('【英语学习】textOnlyMode值:', textOnlyMode)
  }
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      console.log('【英语学习】收到消息:', data)
      
      if (data.type === 'text') {
        // 处理文本块
        onTextChunk({
          text: data.text,
          isComplete: data.isComplete || false
        })
      } else if (data.type === 'complete') {
        // 处理完成信号
        onComplete()
      } else if (data.type === 'voice_data') {
        // 处理音频数据
        console.log('【英语学习】收到音频数据，长度:', data.data.length)
        onAudioChunk(data.data)
      } else if (data.type === 'error') {
        // 处理错误
        console.error('【英语学习】从服务器接收到错误:', data.message)
        onError(data.message || '服务暂时不可用')
      }
    } catch (error) {
      console.error('【英语学习】处理WebSocket消息失败:', error)
      console.error('【英语学习】原始消息内容:', event.data)
      onError('消息处理失败')
    }
  }
  
  ws.onerror = (error) => {
    console.error('【英语学习】WebSocket错误详情:', error)
    onError('连接失败，请检查网络或认证状态')
  }
  
  ws.onclose = (event) => {
    console.log('【英语学习】WebSocket连接关闭:', event.code, event.reason)
    if (event.code !== 1000) {
      // 非正常关闭
      let errorMessage = '连接已断开'
      if (event.code === 1002) {
        errorMessage = '协议错误'
      } else if (event.code === 1003) {
        errorMessage = '不支持的消息类型'
      } else if (event.code === 1005) {
        errorMessage = '缺少状态码'
      } else if (event.code === 1008) {
        errorMessage = '违反策略'
      } else if (event.code === 1011) {
        errorMessage = '服务器内部错误'
      }
      console.error('【英语学习】WebSocket非正常关闭:', event.code, event.reason, errorMessage)
      onError(errorMessage)
    }
  }
  
  return ws
}

// 英语学习请求接口
export interface EnglishLearningRequest {
  message?: string
  speechText?: string
  childAge?: number
  learningMode?: string
  currentTopic?: string
  pronunciationScore?: number
  grammarErrors?: GrammarError[]
  needCorrection?: boolean
  learningDuration?: number
  newWordsCount?: number
  sentencesCount?: number
  grammarMistakesCount?: number
  correctionsAccepted?: number
}

// 语法错误接口
export interface GrammarError {
  error: string
  correction: string
  errorType: string
  explanation: string
  suggestion: string
}

// 知识点接口
export interface KnowledgePoint {
  type: string
  content: string
  contentZh: string
  category: string
  examples: string[]
}

// 学习进度接口
export interface LearningProgress {
  totalDuration: number
  newWordsLearned: number
  sentencesPracticed: number
  avgPronunciationScore: number
  grammarMistakesCount: number
  masteryLevel: number
  difficultyAdjustment: string
}

// 英语学习响应接口
export interface EnglishLearningResponse {
  content: string
  contentEn: string
  pronunciationScore: number
  pronunciationFeedback: string
  grammarErrors: GrammarError[]
  suggestions: string[]
  learningPoints: KnowledgePoint[]
  isTeachingMoment: boolean
  teachingType: string
  audioUrl: string
  progress: LearningProgress
  nextRecommendation: string
  weakPoints: string[]
  sessionEnded: boolean
  sessionSummary: string
}

/**
 * 创建英语学习会话
 */
export function createEnglishSession(data: EnglishLearningRequest): Promise<{ id: number }> {
  return request({
    url: '/api/english-learning/session',
    method: 'post',
    data
  }).then(response => {
    // 提取data字段，因为后端返回的是ResponseDTO格式
    if (response && response.data) {
      console.log('【英语学习】创建会话成功:', response.data)
      return response.data as { id: number };
    }
    console.error('【英语学习】创建会话响应格式错误:', response)
    throw new Error('响应格式错误：缺少data字段');
  }).catch(error => {
    console.error('【英语学习】创建会话请求失败:', error)
    throw error
  });
}

/**
 * 英语对话学习（SSE版本）
 */
export function englishConversation(
  sessionId: number,
  requestData: EnglishLearningRequest,
  onMessage: (response: EnglishLearningResponse) => void,
  onAudio: (audioData: string) => void,
  onComplete: () => void,
  onError: (error: string) => void
): Promise<void> {
  const url = `/api/english-learning/session/${sessionId}/conversation/stream`;
  
  try {
    // 使用fetch发送POST请求并处理SSE响应
    const response = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(requestData)
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No reader available');
      }
      
      let buffer = '';
      let currentEvent = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // 处理 SSE 格式的数据
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          if (trimmedLine.startsWith('event:')) {
            currentEvent = trimmedLine.substring(6).trim();
          } else if (trimmedLine.startsWith('data:')) {
            try {
              const dataStr = trimmedLine.substring(5).trim();
              
              // 处理错误事件
              if (currentEvent === 'error') {
                if (onError) {
                  onError(dataStr || '服务暂时不可用，请稍后再试');
                }
                break;
              }
              
              // 处理音频数据
              if (currentEvent === 'voice_data') {
                if (onAudio) {
                  onAudio(dataStr);
                }
              }
              // 处理正常消息
              else if (currentEvent === 'message') {
                const response = JSON.parse(dataStr) as EnglishLearningResponse;
                onMessage(response);
              }
            } catch (e) {
              console.error('解析SSE数据失败:', e);
              if (onError) {
                onError('数据解析错误');
              }
              break;
            }
          }
        }
      }
      
      onComplete();
    }).catch(error => {
      console.error('英语对话学习请求失败:', error);
      if (onError) {
        onError('网络连接失败，请检查网络后重试');
      }
    });
    
    return response as Promise<void>;
  } catch (error) {
    console.error('英语对话学习请求异常:', error);
    if (onError) {
      onError('请求发送失败');
    }
    return Promise.reject(error);
  }
}

/**
 * 获取用户能力评估
 */
export function getUserAbilityAssessment(): Promise<EnglishLearningResponse> {
  return request({
    url: '/api/english-learning/assessment',
    method: 'get'
  }).then(response => {
    if (response && response.data) {
      console.log('【英语学习】获取用户能力评估成功:', response.data)
      return response.data as EnglishLearningResponse;
    }
    console.error('【英语学习】获取用户能力评估响应格式错误:', response)
    throw new Error('响应格式错误：缺少data字段');
  }).catch(error => {
    console.error('【英语学习】获取用户能力评估请求失败:', error)
    throw error
  });
}

/**
 * 获取每日学习建议
 */
export function getDailyLearningRecommendation(): Promise<EnglishLearningResponse> {
  return request({
    url: '/api/english-learning/recommendation',
    method: 'get'
  }).then(response => {
    if (response && response.data) {
      console.log('【英语学习】获取每日学习建议成功:', response.data)
      return response.data as EnglishLearningResponse;
    }
    console.error('【英语学习】获取每日学习建议响应格式错误:', response)
    throw new Error('响应格式错误：缺少data字段');
  }).catch(error => {
    console.error('【英语学习】获取每日学习建议请求失败:', error)
    throw error
  });
}

/**
 * 结束学习会话
 */
export function endEnglishSession(sessionId: number): Promise<EnglishLearningResponse> {
  return request({
    url: `/api/english-learning/session/${sessionId}/end`,
    method: 'post'
  }).then(response => {
    if (response && response.data) {
      console.log('【英语学习】结束学习会话成功:', response.data)
      return response.data as EnglishLearningResponse;
    }
    console.error('【英语学习】结束学习会话响应格式错误:', response)
    throw new Error('响应格式错误：缺少data字段');
  }).catch(error => {
    console.error('【英语学习】结束学习会话请求失败:', error)
    throw error
  });
}